/**
 * RFC 4180-compliant CSV writer.
 *
 * Why this exists
 * ---------------
 * v0 used the `xlsx` (SheetJS) package, which currently ships two CVEs with no
 * upstream fix (GHSA-4r6h-8v6p-xvw6 prototype pollution, GHSA-5pgg-2g8v-p4x9
 * ReDoS). Even though CORTEX only *writes* spreadsheets (never parses
 * untrusted ones), Lombard's security baseline forbids shipping a package
 * carrying a HIGH CVE with no upstream patch. CSV is the simplest fully
 * audited alternative and is natively openable in Excel and Google Sheets.
 *
 * Server-side, richly formatted `.xlsx` exports are scheduled for the API
 * tier in PR #4 / v2.
 *
 * Compliance notes
 * ----------------
 * - The writer assumes Excel-friendly defaults: CRLF line endings, UTF-8 with
 *   BOM so Excel for Windows auto-detects encoding (POPIA-relevant client
 *   names with diacritics render correctly).
 * - Fields are always quoted; this avoids ambiguity around embedded commas,
 *   quotes, and newlines without per-field branching.
 * - Internal double-quotes are escaped by doubling, per RFC 4180 §2.5.
 * - Number and Date types are serialised via the caller's `toString`-equivalent
 *   so the export is deterministic and locale-independent.
 */

const UTF8_BOM = '\uFEFF';
const LINE_TERMINATOR = '\r\n';

const escapeField = (value: unknown): string => {
  if (value === null || value === undefined) return '""';
  const raw = value instanceof Date ? value.toISOString() : String(value);
  return `"${raw.replace(/"/g, '""')}"`;
};

export interface CsvColumn<TRow> {
  /** Header label rendered in the first row. */
  header: string;
  /** Pure projection from a row to the serialised cell value. */
  accessor: (row: TRow) => string | number | Date | null | undefined;
}

export const buildCsv = <TRow>(
  rows: readonly TRow[],
  columns: readonly CsvColumn<TRow>[],
): string => {
  const headerLine = columns.map((c) => escapeField(c.header)).join(',');
  const bodyLines = rows.map((row) => columns.map((c) => escapeField(c.accessor(row))).join(','));
  return UTF8_BOM + [headerLine, ...bodyLines].join(LINE_TERMINATOR) + LINE_TERMINATOR;
};

/**
 * Triggers a browser download for a CSV string previously produced by
 * `buildCsv`. Kept separate from `buildCsv` so the latter remains pure and
 * unit-testable in jsdom.
 */
export const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
};
